import React, {useEffect, useState} from "react";
import "components/Modals/Contact/Contact.scss";
import Button from "components/reusable/Button/Button";
import {convertHtmlToReact} from "@hedgedoc/html-to-react/dist/convertHtmlToReact";
import { useDispatch } from "react-redux";
import { toggleModal } from "store/reducers/modal/modalSlice";
import { useSubmitContactFormMutation } from "api/beanfield/ContactForm";
import { useLocation } from "react-router";
import ReCAPTCHA from "react-google-recaptcha";
import EmailValidation from "utils/EmailValidation";
import {useGetContactPageData} from "services/api/useGetContactPageData";
import ServiceDropdown from "components/reusable/Dropdown/ServiceDropdown";
import {
  ProductOptions,
  FRProductOptions,
  ReasonOptions,
  FRReasonOptions
} from "utils/DropDownData";
import {WagtailImage} from "components/Wagtail/WagtailImage";
import AddressModal from "components/Modals/Address/Address";

const ContactModal = () => {
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const isFrench = pathname.split("/")[1] === "fr";
  const [submitForm] = useSubmitContactFormMutation();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [messageSent, setMessageSent] = useState(false);
  const [firstNameError, setFirstNameError] = useState(false);
  const [lastNameError, setLastNameError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [phoneError, setPhoneError] = useState(false);
  const [messageError, setMessageError] = useState(false);
  const [reasonError, setReasonError] = useState(false);
  const [emailValid, setEmailValid] = useState(true);
  const [reason, setReason] = useState(null);
  const [product, setProduct] = useState("");
  const [token, setToken] = useState("");
  const [showExtraInfo, setShowExtraInfo] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const recaptchaRef = React.createRef();

  const recaptchaClicked = () => {
    const token = recaptchaRef.current.getValue();
    setToken(token);
  };

  const formIsValid = () => {
    return (firstName && lastName && email && phone && message && reason && token) &&
      (!firstNameError && !lastNameError && !emailError && !phoneError && !messageError && !reasonError) &&
      (emailValid);
  }

  const handleMessage = () => {
    if (formIsValid()) {
      setIsLoading(true);
      submitForm({
        firstName: firstName,
        lastName: lastName,
        email: email,
        telephone: phone,
        reason: reason.value,
        message: message,
        product: (product.length > 0 ? product.map((product) => product.value).join(", ") : ""),
        token: token,
      }).then((result) => {
        setIsLoading(false);
        setMessageSent(true);
      });
    }
  };

  useEffect(() => {
    if (reason)
      setReasonError(false);
  }, [reason])

  const handleClick = () => {
    dispatch(toggleModal());
    dispatch(toggleModal({
      children: <AddressModal />
    }));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
     dispatch(toggleModal({
        children: <AddressModal />
      }));
    }
  };

  const validateField = (field) => {
    switch (field) {
      case "firstName":
        if (firstName === "")
          setFirstNameError(true);
        else
          setFirstNameError(false);
        break;
      case 'lastName':
        if (lastName === "")
          setLastNameError(true);
        else
          setLastNameError(false);
        break;
      case 'email':
        if (email === "")
          setEmailError(true);
        else if (!EmailValidation(email)) {
          setEmailValid(false);
          setEmailError(false);
        }
        else {
          setEmailValid(true);
          setEmailError(false);
        }
        break;
      case 'phone':
        if (phone === "")
          setPhoneError(true);
        else
          setPhoneError(false);
        break;
      case 'message':
        if (message === "")
          setMessageError(true);
        else
          setMessageError(false);
        break;
      case 'reason':
        if (!reason || reason?.value === "")
          setReasonError(true);
        else if (reason?.value === "general_inquiry") {
          setProduct({})
          setReasonError(false);
        }
        else
          setReasonError(false);
        break;
      default:
        return
    }
  }

  //CMS API CALL
  const { data: cmsData } = useGetContactPageData(useLocation)

  useEffect(() => {
    if (reason && reason.value !== 'general_inquiry') {
      setShowExtraInfo(true);
    } else {
      setShowExtraInfo(false);
    }
  },[reason, product])

  return (
    <div className="form-modal-container">
      {messageSent ? (
        <div className="contact-thanks-container" tabIndex="0">
          <h3>{cmsData?.thank_you_message}</h3>
          <p>{cmsData?.thank_you_sub_message}</p>
        </div>
      ) : (
        <div className="contact-form-container">
          <div className="contact-form-header" tabIndex="0">
            <h2>{cmsData?.send_message_header}</h2>

            <p>{cmsData?.send_message_description}</p>

            <p className="contact-warning-text">
              {cmsData?.required_field_text}
            </p>
          </div>

          <div className="contact-form">
            <div className="contact-split-container">
              <div>
                <label htmlFor="firstName">{cmsData?.form_title_fn}</label>
                <input
                  id="firstName"
                  onChange={(e) => setFirstName(e.target.value)}
                  onBlur={() => {
                    validateField("firstName")
                  }}
                  value={firstName}
                  aria-errormessage={firstNameError}
                  aria-label={cmsData?.form_mandatory_fn}
                />
                {firstNameError ? (
                  <p className="contact-error-text" tabIndex="0">
                    {cmsData?.form_error_fn}
                  </p>
                ) : null}
              </div>

              <div>
                <label htmlFor="lastName">{cmsData?.form_title_ln}</label>
                <input
                  id="lastName"
                  onChange={(e) => setLastName(e.target.value)}
                  onBlur={() => {
                    validateField("lastName")
                  }}
                  value={lastName}
                  aria-errormessage={lastNameError}
                  aria-label={cmsData?.form_mandatory_ln}
                />
                {lastNameError ? (
                  <p className="contact-error-text" tabIndex="0">
                    {cmsData?.form_error_ln}
                  </p>
                ) : null}
              </div>
            </div>

            <div className="contact-split-container">
              <div>
                <label htmlFor="email">{cmsData?.form_title_email}</label>
                <input
                  id="email"
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => {
                    validateField("email")
                  }}
                  value={email}
                  aria-errormessage={emailError || !emailValid}
                  aria-label={cmsData?.form_mandatory_email}
                />
                {!emailValid ? (
                  <p className="contact-error-text" tabIndex="0">
                    {cmsData?.form_error_valid_email}
                  </p>
                ) : emailError ? (
                  <p className="contact-error-text" tabIndex="0">
                    {cmsData?.form_error_empty_email}
                  </p>
                ) : null}
              </div>
              <div>
                <label htmlFor="phone">{cmsData?.form_title_phone}</label>
                <input
                  id="phone"
                  onChange={(e) => setPhone(e.target.value)}
                  onBlur={() => {
                    validateField("phone")
                  }}
                  value={phone}
                  aria-errormessage={phoneError}
                  aria-label={cmsData?.form_mandatory_phone}
                />
                {phoneError ? (
                  <p className="contact-error-text" tabIndex="0">
                    {cmsData?.form_error_phone}
                  </p>
                ) : null}
              </div>
            </div>
            <div className="contact-single-container">
              <p className="label">{cmsData?.form_title_reason}</p>
              <ServiceDropdown
                classNamePrefix="reason-dropdown"
                className={reasonError ? "reason-select-dropdown error" : "reason-select-dropdown"}
                placeholder=""
                options={isFrench ? FRReasonOptions : ReasonOptions}
                BGColor={"#fff"}
                textColor={"#000"}
                select={(value) => {
                  setReason(value);
                }}
                onBlur={() => validateField("reason")}
              />
              {reasonError ? (
                <p className="contact-error-text" tabIndex="0">
                  {cmsData?.form_error_valid_reason}
                </p>
              ) : null}
            </div>
            {showExtraInfo ? (
              <div className="contact-single-container">
                <p className="label">{cmsData?.form_title_product}</p>
                <ServiceDropdown
                  classNamePrefix="product-dropdown"
                  className="product-select-dropdown"
                  placeholder=""
                  options={isFrench ? FRProductOptions : ProductOptions}
                  BGColor={"#fff"}
                  textColor={"#000"}
                  isMulti={true}
                  select={(value) => {
                    setProduct(value);
                  }}
                />
              </div>
            ) : null}
            <div className="contact-single-container">
              <div className="message-container">
                <label htmlFor="message">{cmsData?.form_title_message}</label>
                <textarea
                  id="message"
                  onChange={(e) => setMessage(e.target.value)}
                  onBlur={() => {
                    validateField("message")
                  }}
                  value={message}
                  aria-errormessage={messageError}
                  aria-label={cmsData?.form_mandatory_message}
                />
                {messageError ? (
                  <p className="contact-error-text" tabIndex="0">
                    {cmsData?.form_error_message}
                  </p>
                ) : null}
              </div>
            </div>
            {showExtraInfo ? (
              <div className="check-availability-container">
                <div className="left">
                  <div className="image">
                    <WagtailImage height={52} width={52} image={cmsData?.form_check_availability_image}/>
                  </div>
                  <div className="text-description">
                    <div className="title">{convertHtmlToReact(cmsData?.form_check_availability_title)}</div>
                    <div
                      className="description">{convertHtmlToReact(cmsData?.form_check_availability_description)}</div>
                  </div>
                </div>
                <div className="button">
                  <Button
                    btnStyle={"beanfield-button--hollow"}
                    style={{
                      borderColor: "#00663E",
                      fontWeight: 700,
                      color: "#00663E",
                      width: "220px",
                    }}
                    click={() => handleClick()}
                    onKeyDown={(e) => handleKeyDown(e)}
                  >
                    {cmsData?.form_check_availability_btn_text}
                  </Button>
                </div>
              </div>
            ) : null}
            <div className="captcha-container">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={process.env.REACT_APP_RESIDENTIAL_CONTACT_KEY}
                onChange={recaptchaClicked}
              />
            </div>
            <div className="submit-button-container">
              <Button
                style={{width: 210, float: "right"}}
                click={() => handleMessage()}
                disabled={!formIsValid() || isLoading}
                loading={isLoading}
              >
                {cmsData?.form_btn_send}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactModal;
